import { version } from '#base/version.js';
import { TEXT_PLACEHOLDER } from '@c2s-npm/plumlib';
import { fromJS, List } from 'immutable';

export function setAssetLibrary(s, { assetLibrary }) {
  return s.set('assetLibrary', assetLibrary);
}

export function setDesign(s, { design }) {
  return selectFace(s, { face: design?.faces?.[0]?.face }).merge(fromJS(design));
}

export function addFace(s, { config }) {
  const { face, preview } = config;
  const { width, height } = preview;

  const selected = selectFace(s, { face });
  const hasFace = !!s.get('faces').find((f) => f.get('face') === face);

  return hasFace
    ? selected
    : selected.set('faces', s.get('faces').push(fromJS({ face, width, height, elements: [] })));
}

export function removeFace(s, { face }) {
  const filtered = s.set(
    'faces',
    s.get('faces').filter((f) => f.get('face') !== face)
  );
  return selectFace(filtered, { face: filtered.getIn(['faces', 0, 'face']) });
}

export function setFaceSize(s, { face, width, height }) {
  let newState = s;
  const nFaces = s.get('faces').count();
  for (let faceIdx = 0; faceIdx < nFaces; ++faceIdx) {
    if (s.getIn(['faces', faceIdx, 'face']) === face.face) {
      const faceObj = s.getIn(['faces', faceIdx]);
      const nElements = faceObj.get('elements').count();
      const scale = width / faceObj.get('width');
      newState = newState
        .setIn(['faces', faceIdx, 'width'], width)
        .setIn(['faces', faceIdx, 'height'], height)
        .removeIn(['faces', faceIdx, 'meta']);
      for (let elemIdx = 0; elemIdx < nElements; ++elemIdx) {
        const e = faceObj.getIn(['elements', elemIdx]);
        newState = newState
          .setIn(['faces', faceIdx, 'elements', elemIdx, 'x'], e.get('x') * scale)
          .setIn(['faces', faceIdx, 'elements', elemIdx, 'y'], e.get('y') * scale)
          .setIn(['faces', faceIdx, 'elements', elemIdx, 'w'], e.get('w') * scale)
          .setIn(['faces', faceIdx, 'elements', elemIdx, 'h'], e.get('h') * scale);
        if ((e.get('fixedFontSizePt') || 0) > 0) {
          newState = newState.setIn(
            ['faces', faceIdx, 'elements', elemIdx, 'fixedFontSizePt'],
            e.get('fixedFontSizePt') * scale
          );
        }
      }
    }
  }
  return newState;
}

export function selectFace(s, { face }) {
  return s
    .set('selectedFace', face)
    .set('selectedElements', null)
    .set('hasSelection', false)
    .set('isMulti', false);
}

export function setActiveGroup(s, { index }) {
  return s.update('faces', (faces) => {
    return faces.map((face) => {
      return face.update('elements', (elements) => {
        return elements.map((element) => {
          if (element.get('isGroup')) {
            return element.set(
              'data',
              element
                .get('data')
                .map((data, i) => (i === index ? data.set('active', true) : data.delete('active')))
            );
          } else {
            return element;
          }
        });
      });
    });
  });
}

export function selectElement(s, { face, id }) {
  return selectElements(s, { face, ids: id ? [id] : null });
}

export function selectElements(s, { face, ids }) {
  if (ids?.length > 0) {
    const selected = s.get('selectedElements');
    if (selected && ids.length === selected.length && ids.every((id) => selected.includes(id))) {
      return s;
    }
  }

  // If a selected object is a font object that has the default text, remove it.
  var data = selectFace(s, { face });
  const selected = s.get('selectedElements');
  if (selected?.length > 0) {
    const emptyObjIds = new Set(
      selected.filter((id) => {
        const faceObj = s
          .get('faces')
          .filter((o) => o.get('face') === face)
          ?.first();
        const el = faceObj
          .get('elements')
          .filter((o) => o.get('id') === id)
          ?.first();
        const type = el?.get('elementType');
        const data = el?.get('data');
        // Only remove font objects. A monogram with the default "ABC" could be a
        // real person (say, Albert Christian Baker), so we cannot remove them.
        return type === 'font' && data === TEXT_PLACEHOLDER;
      })
    );
    if (emptyObjIds?.size > 0) {
      const toRemove = fromJS(emptyObjIds).toSeq();
      data = removeElements(data, { face, ids: toRemove });
      const toSelect = ids?.filter((id) => !emptyObjIds.contains(id));
      if (toSelect) {
        ids = toSelect;
      }
    }
  }

  data = moveElementsToFront(data, { face, ids });

  if (ids?.length > 0) {
    return data
      .set('selectedElements', ids)
      .set('hasSelection', true)
      .set('isMulti', ids?.length > 1);
  } else {
    return selectNone(data);
  }
}

export function selectNone(s) {
  return s
    .set('selectedElements', null)
    .set('hasSelection', false)
    .set('isMulti', false)
    .set('selectionCleared', true);
}

export function updateSelectedElement(s, { face, data }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  const idata = fromJS(data);
  const elements = s.getIn(['faces', faceIndex, 'elements']);

  const allElements = s.get('faces').reduce((a, f) => a.concat(f.get('elements')), new List());
  
  const selected =
    !s.get('isMulti') && elements.find((e) => e.get('id') === s.get('selectedElements')?.[0]);
  const id = selected?.get?.('elementType') === data.elementType && selected?.get?.('id');
  if (id) {
    const elementIndex = elements.findIndex((e) => e.get('id') === id);
    return selectElement(s, { face, id }).mergeIn(['faces', faceIndex, 'elements', elementIndex], idata);
  } else {
    const newId = (allElements.map((e) => e.get('id')).max() || 0) + 1;

    const lastIndex = ['font', 'monogram', 'jersey'].includes(data.elementType)
      ? allElements.size
      : allElements.findLastIndex((e) => ['font', 'monogram', 'jersey'].includes(e.get('elementType')));

    return selectElement(s, { face, id: newId }).setIn(
      ['faces', faceIndex, 'elements'],
      elements.insert(lastIndex || 0, idata.set('id', newId).set('version', version))
    );
  }
}

export function setElement(s, { face, id, data }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  const elements = s.getIn(['faces', faceIndex, 'elements']);
  const elementIndex = elements.findIndex((e) => e.get('id') === id);
  const idata = fromJS(data);
  return s.setIn(['faces', faceIndex, 'elements', elementIndex], idata);
}

export function positionElement(s, { face, id, data }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  const elements = s.getIn(['faces', faceIndex, 'elements']);
  const elementIndex = elements.findIndex((e) => e.get('id') === id);
  if (elementIndex >= 0) {
    const idata = fromJS(data);
    return s.mergeIn(['faces', faceIndex, 'elements', elementIndex], idata);
  } else {
    return s;
  }
}

export function positionElements(s, { face, elements }) {
  return elements.reduce((s, { id, data }) => positionElement(s, { face, id, data }), s);
}

export function removeElement(s, { face, id }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  return selectFace(s, { face }).setIn(
    ['faces', faceIndex, 'elements'],
    s.getIn(['faces', faceIndex, 'elements']).filter((e) => e.get('id') !== id)
  );
}

export function removeElements(s, { face, ids }) {
  return ids.reduce((a, id) => removeElement(a, { face, id }), s);
}

export function moveElementsToFront(s, { face, ids }) {
  return reorderElementsToEnd(s, { face, ids, dir: 'front' });
}

export function moveElementsToBack(s, { face, ids }) {
  return reorderElementsToEnd(s, { face, ids, dir: 'back' });
}

export function reorderElementsToEnd(s, { face, ids, dir }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);

  const elements = s.getIn(['faces', faceIndex, 'elements']);

  const included = elements.filter((e) => (ids || []).includes(e.get('id')));
  const excluded = elements.filter((e) => !(ids || []).includes(e.get('id')));

  // lower index gets rendered first so it will be behind
  return s.setIn(
    ['faces', faceIndex, 'elements'],
    dir === 'back' ? included.concat(excluded) : excluded.concat(included)
  );
}

export function moveElementForward(s, { face, id }) {
  return reorderElements(s, { face, id, dir: 'forward' });
}

export function moveElementBack(s, { face, id }) {
  return reorderElements(s, { face, id, dir: 'back' });
}

export function reorderElements(s, { face, id, dir }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);

  const elements = s.getIn(['faces', faceIndex, 'elements']);
  const elementIndex = elements.findIndex((e) => e.get('id') === id);

  // lower index gets rendered first so it will be behind
  if (dir === 'back') {
    if (elementIndex > 0) {
      const start = elements.take(elementIndex - 1);
      const end = elements.skip(elementIndex + 1);
      const swap1 = elements.get(elementIndex - 1);
      const swap2 = elements.get(elementIndex);
      return s.setIn(['faces', faceIndex, 'elements'], start.concat([swap2, swap1], end));
    }
  } else {
    if (elementIndex < elements.count() - 1) {
      const start = elements.take(elementIndex);
      const end = elements.skip(elementIndex + 2);
      const swap1 = elements.get(elementIndex);
      const swap2 = elements.get(elementIndex + 1);
      return s.setIn(['faces', faceIndex, 'elements'], start.concat([swap2, swap1], end));
    }
  }
  return s;
}

export function resetElement(s, { face, id }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  const elements = s.getIn(['faces', faceIndex, 'elements']);
  const elementIndex = elements.findIndex((e) => e.get('id') === id);
  const element = elements.get(elementIndex);
  return s.setIn(
    ['faces', faceIndex, 'elements', elementIndex],
    element.delete('x').delete('y').delete('scale').delete('rotation')
  );
}

export function updateMeta(s, { face, meta }) {
  const faceIndex = s.get('faces').findIndex((f) => f.get('face') === face);
  return s.setIn(['faces', faceIndex, 'meta'], meta);
}
